/**
 * card-product-swiper — TR-4
 *
 * Ürün kartındaki görsel şeridini mobilde yönetir: kaydırma konumuna göre
 * sağ alttaki nokta göstergesini günceller ve kaydırma sonrası oluşabilecek
 * istemsiz tıklamayı engeller. Masaüstünde tamamen devre dışı kalır.
 *
 * Dokunmatik cihazlarda kaydırma tarayıcının kendi scroll'u ile çalışır.
 * Fare ile açılan dar ekranlarda (masaüstü responsive görünüm, tema editörü
 * mobil önizleme) native scroll sürüklenemediği için sürükleme ayrıca
 * uygulanır ve bırakınca en yakın görsele oturtulur.
 */
if (!customElements.get('card-product-swiper')) {
  const ALIGN_DELAY = 180;
  const ALIGN_MAX_RETRIES = 5;

  class CardProductSwiper extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.rafId = null;
      this.active = false;
      this.swiped = false;
      this.onScroll = this.onScroll.bind(this);
      this.onBreakpointChange = this.onBreakpointChange.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.onTouchCancel = this.onTouchCancel.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onDragStart = this.onDragStart.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onScrollEnd = this.onScrollEnd.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
      this.dragging = false;
      this.touching = false;
      this.alignTimer = null;
      this.alignRetries = 0;
      this.resizeObserver = null;
      this.hydrated = false;
    }

    connectedCallback() {
      this.track = this.querySelector('.card-product__swiper-track');
      this.slides = Array.from(this.querySelectorAll('.card-product__swiper-slide'));
      this.dots = Array.from(this.querySelectorAll('.card-product__swiper-dot'));

      if (!this.track || this.dots.length < 2) return;

      // Tema slider'ı içindeki kartlarda iç içe yatay kaydırma çakışır;
      // bu kartlarda kaydırma devre dışı bırakılır (bkz. card-product-swiper.css).
      if (this.closest('splide-carousel, .splide')) return;

      this.mediaQuery = window.matchMedia('(max-width: 767px)');
      this.mediaQuery.addEventListener('change', this.onBreakpointChange);
      this.onBreakpointChange();
    }

    disconnectedCallback() {
      this.stop();
      this.mediaQuery?.removeEventListener('change', this.onBreakpointChange);
    }

    onBreakpointChange() {
      if (this.mediaQuery.matches) {
        this.start();
      } else {
        this.stop();
      }
    }

    start() {
      if (this.active) return;
      this.active = true;

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.track.addEventListener('touchend', this.onTouchEnd, { passive: true });
      this.track.addEventListener('touchcancel', this.onTouchCancel, { passive: true });
      this.track.addEventListener('click', this.onClickCapture, true);
      this.track.addEventListener('pointerdown', this.onPointerDown);
      this.track.addEventListener('dragstart', this.onDragStart);

      // Şeridin iki slayt arasında kalmasına karşı emniyet. iOS Safari,
      // dikey kaydırma sırasında kaba küçük bir yatay hareket geçtiğinde veya
      // geç yüklenen görsel yerleşimi değiştirdiğinde her zaman en yakın
      // slayta geri oturtmuyor; arada kalınca iki görsel yan yana yarım
      // görünüyor. Kaydırma durduğunda konum kontrol edilip düzeltilir.
      if ('onscrollend' in window) {
        this.track.addEventListener('scrollend', this.onScrollEnd);
      }

      // Geri/ileri gezinmede ve uygulama içi tarayıcıların sayfayı geri
      // yüklemesinde şerit kaydedilmiş konuma dönüyor; o konum arada kalmış
      // olabiliyor ve hiçbir scroll olayı ateşlenmiyor.
      window.addEventListener('pageshow', this.onPageShow);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.track);
        // Track'in genişliği grid hücresine sabit olduğu için tek başına
        // izlemek yetmiyor; slaydın kendisi de izlenir.
        if (this.slides[0]) this.resizeObserver.observe(this.slides[0]);
      }
    }

    stop() {
      if (!this.active) return;
      this.active = false;

      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('touchstart', this.onTouchStart);
      this.track.removeEventListener('touchend', this.onTouchEnd);
      this.track.removeEventListener('touchcancel', this.onTouchCancel);
      this.track.removeEventListener('click', this.onClickCapture, true);
      this.track.removeEventListener('pointerdown', this.onPointerDown);
      this.track.removeEventListener('dragstart', this.onDragStart);
      this.track.removeEventListener('scrollend', this.onScrollEnd);
      window.removeEventListener('pageshow', this.onPageShow);

      this.resizeObserver?.disconnect();
      this.resizeObserver = null;

      if (this.alignTimer) {
        clearTimeout(this.alignTimer);
        this.alignTimer = null;
      }

      this.endDrag();
      this.touching = false;

      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }

      this.track.scrollLeft = 0;
      this.setActiveDot(0);
    }

    /**
     * Slayt genişliği. clientWidth ve offsetLeft tam sayıya yuvarladığı için
     * kesirli genişliklerde kullanılamaz: 402px viewport'ta gerçek genişlik
     * 167.73px iken clientWidth 168 döndürüyor ve sapma slayt başına birikip
     * hizalamayı yanlış hedefe götürüyor. Ölçüm rect'ten alınır.
     *
     * Üst öğede ölçek dönüşümü varsa rect yanıltır; sapma büyükse yuvarlanmış
     * değere dönülür.
     */
    getSlideWidth() {
      const slide = this.slides[0];
      const fallback = this.track.clientWidth;
      if (!slide) return fallback;

      const measured = slide.getBoundingClientRect().width;
      if (!measured) return fallback;

      return Math.abs(measured - fallback) > 2 ? fallback : measured;
    }

    /**
     * Slayt indeksinin kaydırma hedefi. scrollWidth ve clientWidth yuvarlandığı
     * için son slaydın ideal hedefi ulaşılabilir aralığın bir miktar dışında
     * kalabiliyor; hedef aralığa kısıtlanır ki ulaşılamayan bir noktaya
     * kaydırma denenmesin.
     */
    slideTarget(index, slideWidth) {
      const maxScroll = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
      return Math.min(index * slideWidth, maxScroll);
    }

    /**
     * TR-5: 3. ve sonraki slaytlar kaynaksız basılır (bkz.
     * card-product-media-swiper.liquid). Koleksiyon sayfasında kart başına 8
     * görselin birden inmesi mobil tarayıcıyı bellekten düşürüyordu; şerit
     * yatay olarak ekran dışında olduğu hâlde tarayıcı bu görselleri yine de
     * indiriyor. Kaynak, kullanıcı o kartla etkileşime geçtiğinde yazılır:
     * parmağın değmesi, farenin basması veya şeridin herhangi bir sebeple
     * kayması yeterli. Bu, 3. görsel görünür hâle gelmeden önce gerçekleşir.
     */
    hydrateDeferredImages() {
      if (this.hydrated) return;
      this.hydrated = true;

      this.track.querySelectorAll('img[data-src]').forEach((img) => {
        const { src, srcset } = img.dataset;

        // srcset önce yazılır ki tarayıcı seçimi doğrudan doğru adaydan yapsın.
        if (srcset) img.setAttribute('srcset', srcset);
        if (src) img.setAttribute('src', src);

        delete img.dataset.src;
        delete img.dataset.srcset;
      });
    }

    onScroll() {
      this.hydrateDeferredImages();

      if (this.rafId) return;

      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.syncActiveDot();
      });

      this.scheduleAlign();
    }

    /** Bulunulan slaydı konumdan hesaplayıp nokta göstergesini eşitler. */
    syncActiveDot() {
      const slideWidth = this.getSlideWidth();
      if (!slideWidth) return;

      // RTL'de scrollLeft negatif olabildiği için mutlak değer alınır.
      this.setActiveDot(Math.round(Math.abs(this.track.scrollLeft) / slideWidth));
    }

    /**
     * Kaydırma durduğunda hizalamayı kontrol eder. scrollend olayı olmayan
     * tarayıcılarda zamanlayıcı bu görevi üstlenir.
     */
    scheduleAlign() {
      this.alignRetries = 0;
      this.queueAlign();
    }

    /**
     * Parmak ekrandayken atlanan hizalamayı tekrar dener. Tavan olmadan,
     * bayrak beklenmedik bir şekilde takılı kalırsa zamanlayıcı sonsuza
     * kadar kendini yeniler.
     */
    retryAlign() {
      if (this.alignRetries >= ALIGN_MAX_RETRIES) return;
      this.alignRetries += 1;
      this.queueAlign();
    }

    queueAlign() {
      if (this.alignTimer) clearTimeout(this.alignTimer);
      this.alignTimer = setTimeout(() => {
        this.alignTimer = null;
        this.alignToNearestSlide();
      }, ALIGN_DELAY);
    }

    onScrollEnd() {
      this.alignToNearestSlide();
    }

    onPageShow() {
      // Geri yüklenen konum arada kalmış olabilir; nokta göstergesi de
      // konumdan yeniden hesaplanmalı, aksi halde şerit 3. görselde dururken
      // gösterge 1. görseli işaret ediyor.
      this.syncActiveDot();
      this.scheduleAlign();
    }

    /**
     * Şerit iki slaydın arasında kaldıysa en yakın slayta oturtur.
     * Parmak ekrandayken veya fare ile sürüklenirken karışmaz; o durumda
     * sessizce düşmek yerine tekrar denenir.
     */
    alignToNearestSlide() {
      if (!this.active) return;

      if (this.touching || this.dragging) {
        this.retryAlign();
        return;
      }

      const slideWidth = this.getSlideWidth();
      if (!slideWidth) return;

      const current = this.track.scrollLeft;
      const direction = current < 0 ? -1 : 1;
      const distance = Math.abs(current);
      const index = Math.min(Math.round(distance / slideWidth), this.slides.length - 1);
      const target = this.slideTarget(index, slideWidth);

      this.setActiveDot(index);

      // Cihaz pikseli yuvarlamasından gelen 1px altı fark hizalı sayılır.
      if (Math.abs(distance - target) <= 1) return;

      this.track.scrollTo({ left: target * direction, behavior: 'smooth' });
    }

    /**
     * Geç yüklenen görseller şeridin ölçüsünü değiştirdiğinde bulunulan
     * slayta yeniden hizalar; aksi halde şerit arada kalabiliyor. Burada
     * konumdan değil saklanan indeksten hesaplanır, çünkü mevcut scrollLeft
     * artık eski genişliğe ait.
     */
    onResize() {
      if (this.touching || this.dragging) return;

      const slideWidth = this.getSlideWidth();
      if (!slideWidth) return;

      const direction = this.track.scrollLeft < 0 ? -1 : 1;
      const target = this.index * slideWidth * direction;

      if (Math.abs(this.track.scrollLeft - target) > 1) {
        this.track.scrollLeft = target;
      }
    }

    setActiveDot(nextIndex) {
      const index = Math.min(Math.max(nextIndex, 0), this.dots.length - 1);
      if (index === this.index) return;

      this.dots[this.index]?.classList.remove('is-active');
      this.dots[index].classList.add('is-active');
      this.index = index;
    }

    onTouchStart(event) {
      this.hydrateDeferredImages();
      this.swiped = false;
      this.touching = true;
      this.touchStartX = event.touches[0].clientX;
      this.touchStartScroll = this.track.scrollLeft;
    }

    onTouchEnd(event) {
      this.endTouch(event, false);
    }

    /**
     * iOS, karta değen bir dokunuşu sayfanın dikey kaydırmasına devrettiğinde
     * touchend yerine touchcancel üretiyor. Bu olay dinlenmediği sürece
     * `touching` bayrağı true'da kilitleniyor ve hizalama emniyeti — hem
     * scrollend hem zamanlayıcı yolu — o kartta kalıcı olarak devre dışı
     * kalıyor; şerit arada kaldıysa öyle kalıyor.
     */
    onTouchCancel(event) {
      this.endTouch(event, true);
    }

    endTouch(event, cancelled) {
      const endX = event.changedTouches[0]?.clientX ?? this.touchStartX;
      const movedByFinger = Math.abs(endX - this.touchStartX);
      const movedByScroll = Math.abs(this.track.scrollLeft - this.touchStartScroll);
      const moved = movedByFinger > 10 || movedByScroll > 5;

      // İptal edilen dokunuş da kaydırma sayılır; bayrak sıfırlanırsa jest
      // tıklamaya dönüşüp ürün sayfasına gidebilir.
      this.swiped = cancelled ? this.swiped || moved : moved;
      this.touching = false;

      // Momentum kaydırması bitince hizalama kontrol edilir.
      this.scheduleAlign();
    }

    /* Fare ile sürükleme — dokunmatik girdi tarayıcının kendi scroll'una bırakılır. */
    onPointerDown(event) {
      if (event.pointerType === 'touch' || event.button !== 0) return;

      this.hydrateDeferredImages();

      if (this.track.scrollWidth <= this.track.clientWidth) return;

      this.dragging = true;
      this.dragMoved = false;
      this.dragStartX = event.clientX;
      this.dragStartScroll = this.track.scrollLeft;
      this.track.classList.add('is-dragging');
      // Sürükleme sırasında snap kapatılır, bırakınca geri açılır.
      this.track.style.scrollSnapType = 'none';

      // Tarayıcının kendi sürükleme/seçim davranışı devreye girip pointer'ı
      // iptal etmesin diye varsayılan engellenir ve hareket window'dan izlenir;
      // setPointerCapture kaydırma kabında pointercancel'a yol açıyor.
      event.preventDefault();
      window.addEventListener('pointermove', this.onPointerMove, { passive: false });
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      if (!this.dragging) return;

      const distance = event.clientX - this.dragStartX;
      if (Math.abs(distance) > 3) this.dragMoved = true;

      this.track.scrollLeft = this.dragStartScroll - distance;
      event.preventDefault();
    }

    onPointerUp() {
      if (!this.dragging) return;

      this.endDrag();

      const slideWidth = this.getSlideWidth();
      if (slideWidth) {
        const direction = this.track.scrollLeft < 0 ? -1 : 1;
        const index = Math.min(
          Math.round(Math.abs(this.track.scrollLeft) / slideWidth),
          this.slides.length - 1
        );
        this.track.scrollTo({ left: this.slideTarget(index, slideWidth) * direction, behavior: 'smooth' });
      }

      if (this.dragMoved) this.swiped = true;
    }

    endDrag() {
      if (!this.dragging) return;
      this.dragging = false;
      this.track.classList.remove('is-dragging');
      this.track.style.scrollSnapType = '';
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
    }

    onDragStart(event) {
      // Görsellerin tarayıcı içi sürükle-bırak hayaletini engelle.
      event.preventDefault();
    }

    onClickCapture(event) {
      if (!this.swiped) return;

      // Kaydırma sonrası ürün sayfasına gitmeyi engelle.
      event.preventDefault();
      event.stopPropagation();
      this.swiped = false;
    }
  }

  customElements.define('card-product-swiper', CardProductSwiper);
}
